import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';

/**
 * Help article search widget for SellerNavSidebar.
 * Only rendered when a non-empty search query is entered.
 * Debounces API requests by 3 seconds (3000ms) to the dedicated seller help endpoint.
 *
 * @param {Object} props
 * @param {string} props.query Active search query
 */
export default function SellerNavHelpArticles({ query }) {
  const { fetch: authFetch, API_URL } = useAuth();
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      setArticles([]);
      setIsLoading(false);
      setHasSearched(false);
      setError(null);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    let isCanceled = false;
    const abortController = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: trimmed });
        const endpoint = `${API_URL || ''}/v1/seller/help?${params.toString()}`;
        const response = await authFetch(endpoint, {
          signal: abortController.signal,
        });

        if (isCanceled) return;

        if (!response.ok) {
          throw new Error(`Failed to load help articles: ${response.status}`);
        }

        const json = await response.json();
        if (isCanceled) return;

        let results = [];
        if (Array.isArray(json?.data)) {
          results = json.data;
        } else if (json?.data && typeof json.data === 'object') {
          results = [json.data];
        }

        setArticles(results);
        setHasSearched(true);
      } catch (err) {
        if (isCanceled || err.name === 'AbortError') return;
        setError(err.message || 'Error fetching help articles');
        setArticles([]);
        setHasSearched(true);
      } finally {
        if (!isCanceled) {
          setIsLoading(false);
        }
      }
    }, 3000);

    return () => {
      isCanceled = true;
      clearTimeout(timer);
      abortController.abort();
    };
  }, [query, API_URL, authFetch]);

  if (!query || !query.trim()) {
    return null;
  }

  return (
    <div className="seller-nav-help-articles" aria-label="Help articles">
      <div className="seller-nav-help-articles__header">
        <span className="seller-nav-help-articles__icon" aria-hidden="true">
          <i className="fa-solid fa-circle-question"></i>
        </span>
        <span className="seller-nav-help-articles__title">Help Articles</span>
        {isLoading && (
          <span className="seller-nav-help-articles__loading-indicator" aria-label="Searching">
            …
          </span>
        )}
      </div>

      {isLoading && !hasSearched && (
        <div className="seller-nav-help-articles__status">Searching help articles…</div>
      )}

      {error && !isLoading && (
        <div className="seller-nav-help-articles__status seller-nav-help-articles__status--error">
          Failed to load help articles
        </div>
      )}

      {!isLoading && hasSearched && articles.length === 0 && !error && (
        <div className="seller-nav-help-articles__status">No help articles found</div>
      )}

      {articles.length > 0 && (
        <ul className="seller-nav-help-articles__list">
          {articles.map((article) => (
            <li
              key={article.slug || article.uuid || article.id}
              className="seller-nav-help-articles__item"
            >
              <a
                href={`/help/${article.slug || ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="seller-nav-help-articles__link"
              >
                <span className="seller-nav-help-articles__item-title">
                  {article.title}
                </span>
                {article.subtitle && (
                  <span className="seller-nav-help-articles__item-subtitle">
                    {article.subtitle}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
